import { TRPCClientError } from '@trpc/client';
import { getRecordValue, isRecord } from '@/lib/utils/type-guards';

/**
 * Extract timeline error message from TRPCClientError data
 * Returns the error message if present, undefined otherwise
 */
export function extractTimelineErrorMessage(error: unknown): string | undefined {
  if (!(error instanceof TRPCClientError)) {
    return;
  }
  if (!isRecord(error.data)) {
    return;
  }
  const timelineErrorValue = getRecordValue(error.data, 'timelineError');
  const timelineError = isRecord(timelineErrorValue) ? timelineErrorValue : null;
  if (!timelineError) {
    return;
  }
  const message = getRecordValue(timelineError, 'message');
  return typeof message === 'string' ? message : undefined;
}

/**
 * Extract an HTTP status code from TRPC error metadata when available.
 */
export function extractTrpcHttpStatus(error: unknown): number | null {
  if (!(error instanceof TRPCClientError)) {
    return null;
  }
  if (!isRecord(error.data)) {
    return null;
  }
  const status = getRecordValue(error.data, 'httpStatus');
  return typeof status === 'number' ? status : null;
}

/**
 * Throw a proper Error if the TRPCClientError contains a timeline error message
 * Otherwise rethrow the original error
 */
export function handleTimelineError(error: unknown): never {
  const message = extractTimelineErrorMessage(error);
  if (message) {
    throw new Error(message);
  }
  throw error;
}
